import React, { useContext, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { AppSettings } from '../../config/app-settings.js';

const Dealership = () => {
    const context = useContext(AppSettings);
    const { topic } = useParams();
    
    const getDealershipName = (topicParam) => {
        const dealershipMapping = {
            'a1autosales': 'A1 Auto Sales',
            'augustamotors': 'Augusta Motors',
            'auto4youinc': 'Auto4You',
            'autoamerica': 'Auto America',
            'autosbuydon': 'Autos Buy Don',
            'billscarsandtrucks': 'Bills Cars and Trucks',
            'cherokeeautogroup': 'Cherokee Auto Group',
            'creditsolutionautosales': 'Credit Solution Auto Sales',
            'dandsautobrokersllc': 'D&S Auto Brokers',
            'drivecrescent': 'Drive Crescent',
            'elcompadretrucks': 'El Compadre Trucks',
            'ellisoncars': 'Ellison Cars',
            'flyingwheels': 'Flying Wheels',
            'gacertifiedmotors': 'Georgia Certified Motors',
            'galeforcemotors': 'Gale Force Motors',
            'gidieonsgo': 'Gidieon\'s Go & Tow',
            'kellysobxauto': 'Kelly\'s Automotive',
            'marchmotors': 'March Motors',
            'meritautogroup': 'Merit Auto Group',
            'mightymotorauto': 'Mighty Motor Auto Sales',
            'phoenixautomall': 'Phoenix Auto Mall',
            'shopautonet': 'Auto Net',
            'sterlingautosalesoffl': 'Sterling Auto Sales',
            'tagautogroup': 'TAG Auto Group',
            'theleaderdealer': 'The Leader Dealer'
        };
        
        return dealershipMapping[topicParam] || 'Gale Force Motors';
    };
    
    const dealershipName = getDealershipName(topic);
    
    // Handle layout settings
    useEffect(() => {
        context.setAppHeaderNone(true);
        context.setAppSidebarNone(true);
        context.setAppThemePanelNone(true);
        
        return () => {
            context.setAppHeaderNone(false);
            context.setAppSidebarNone(false);
            context.setAppThemePanelNone(false);
        };
    }, [context]);
    
    // Handle script loading
    useEffect(() => {
        // Set global variable for the chat agent to use
        window.dealershipTopic = topic;
        
        // const scriptSrc = `https://galeforceai-public.s3.us-east-2.amazonaws.com/scripts/gale-force-ai-chat-agent.js?v=${Date.now()}`;
        const scriptSrc = 'https://galeforceai-public.s3.us-east-2.amazonaws.com/scripts/gale-force-ai-chat-agent.js';
        
        // Check if script already exists
        const existingScript = document.querySelector(`script[src="${scriptSrc}"]`);
        if (existingScript) {
            return; // Script already loaded, don't add again
        }
        
        const script = document.createElement('script');
        script.src = scriptSrc;
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);

        return () => {
            // Only remove script if it exists and was added by this component
            const scriptToRemove = document.querySelector(`script[src="${scriptSrc}"]`);
            if (scriptToRemove && document.head.contains(scriptToRemove)) {
                document.head.removeChild(scriptToRemove);
            }
        };
    }, [topic]); // Add topic as dependency

    // Add dynamic CSS for hover effects
    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
            .dealership-page .nav-link:hover {
                color: #feca57 !important;
                background: rgba(254, 202, 87, 0.1) !important;
            }
            .dealership-page .cta-button:hover {
                transform: translateY(-2px) !important;
                box-shadow: 0 15px 40px rgba(255, 107, 107, 0.4) !important;
            }
            .dealership-page .vehicle-card:hover {
                transform: translateY(-10px) !important;
                box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3) !important;
                border-color: rgba(255, 107, 107, 0.3) !important;
            }
            .dealership-page .vehicle-button:hover {
                background: linear-gradient(135deg, #764ba2, #667eea) !important;
                transform: translateY(-2px) !important;
            }
            .dealership-page .service-card:hover {
                transform: translateY(-5px) !important;
                background: rgba(255, 255, 255, 0.1) !important;
            }
            .dealership-page .footer-link:hover {
                color: #feca57 !important;
            }
            @media (max-width: 768px) {
                .dealership-page .hero-responsive {
                    flex-direction: column !important;
                    text-align: center !important;
                    padding: 2rem 1rem !important;
                }
                .dealership-page .hero-content-mobile {
                    max-width: 100% !important;
                    margin-bottom: 2rem !important;
                }
                .dealership-page .hero-image-container-mobile {
                    padding-left: 0 !important;
                    max-width: 90% !important;
                }
                .dealership-page .hero-car-image-mobile {
                    transform: none !important;
                }
            }
            
            /* Ensure chat widget is not affected by our styles */
            [id*="chat"], [class*="chat"], [id*="widget"], [class*="widget"] {
                position: fixed !important;
                z-index: 999999 !important;
            }
        `;
        document.head.appendChild(style);

        return () => {
            if (document.head.contains(style)) {
                document.head.removeChild(style);
            }
        };
    }, []);

    const styles = {
        body: {
            margin: 0,
            padding: 0,
            width: '100vw',
            height: '100%',
            fontFamily: "'Inter', 'Segoe UI', 'Roboto', sans-serif",
            backgroundColor: '#3a3a3a',
            color: '#ffffff',
            boxSizing: 'border-box',
            lineHeight: 1.6
        },
        header: {
            background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
            backdropFilter: 'blur(10px)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            padding: '1rem 0',
            position: 'sticky',
            top: 0,
            zIndex: 1000,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
        },
        headerContent: {
            maxWidth: '1200px',
            margin: '0 auto',
            padding: '0 2rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
        },
        logo: {
            fontSize: '2rem',
            fontWeight: 'bold',
            background: 'linear-gradient(135deg, #ff6b6b, #feca57)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
        },
        nav: {
            display: 'flex',
            gap: '2rem'
        },
        navLink: {
            color: '#ffffff',
            textDecoration: 'none',
            fontWeight: '500',
            transition: 'all 0.3s ease',
            padding: '0.5rem 1rem',
            borderRadius: '8px',
            position: 'relative'
        },
        hero: {
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            height: '90vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'relative',
            overflow: 'hidden',
            padding: '0 2rem'
        },
        heroOverlay: {
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 30% 20%, rgba(255, 107, 107, 0.3) 0%, transparent 50%), radial-gradient(circle at 70% 80%, rgba(254, 202, 87, 0.3) 0%, transparent 50%)',
            pointerEvents: 'none'
        },
        heroContent: {
            position: 'relative',
            zIndex: 2,
            maxWidth: '500px',
            padding: '0',
            flex: '1',
            textAlign: 'center'
        },
        heroImageContainer: {
            position: 'relative',
            zIndex: 2,
            flex: '1',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            paddingLeft: '2rem'
        },
        heroCarImage: {
            maxWidth: '100%',
            height: 'auto',
            borderRadius: '0',
            boxShadow: '0 25px 60px rgba(0, 0, 0, 0.4), 0 0 40px rgba(102, 126, 234, 0.3)',
            transition: 'all 0.3s ease',
            transform: 'none',
            filter: 'drop-shadow(0 0 20px rgba(254, 202, 87, 0.2))'
        },
        heroH1: {
            fontSize: 'clamp(2.2rem, 5vw, 3.5rem)',
            marginBottom: '1rem',
            fontWeight: 'bold',
            background: 'linear-gradient(135deg, #ffffff, #f0f0f0)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            lineHeight: 1.2,
            textAlign: 'center'
        },
        heroP: {
            fontSize: '1.1rem',
            marginBottom: '2rem',
            opacity: 0.9,
            maxWidth: '600px',
            margin: '0 auto 2rem'
        },
        ctaButton: {
            background: 'linear-gradient(135deg, #ff6b6b, #feca57)',
            color: '#ffffff',
            padding: '1rem 2.5rem',
            textDecoration: 'none',
            fontSize: '1.1rem',
            borderRadius: '50px',
            border: 'none',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            display: 'inline-block',
            fontWeight: '600',
            boxShadow: '0 10px 30px rgba(255, 107, 107, 0.3)',
            transform: 'translateY(0)'
        },
        contentSection: {
            padding: '5rem 2rem',
            maxWidth: '1200px',
            margin: '0 auto'
        },
        sectionTitle: {
            fontSize: '2.5rem',
            marginBottom: '3rem',
            textAlign: 'center',
            background: 'linear-gradient(135deg, #ff6b6b, #feca57)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontWeight: 'bold'
        },
        vehicleGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
            gap: '2rem',
            marginTop: '3rem'
        },
        vehicleCard: {
            background: 'linear-gradient(135deg, #1e1e1e 0%, #2d2d2d 100%)',
            borderRadius: '20px',
            padding: '2rem',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            transition: 'all 0.3s ease',
            position: 'relative',
            overflow: 'hidden'
        },
        vehicleCardH3: {
            fontSize: '1.5rem',
            marginBottom: '1rem',
            color: '#ffffff',
            fontWeight: '600'
        },
        vehicleCardP: {
            marginBottom: '1rem',
            opacity: 0.8,
            lineHeight: 1.6
        },
        vehicleCardPrice: {
            fontSize: '1.3rem',
            fontWeight: 'bold',
            color: '#feca57',
            marginBottom: '1.5rem'
        },
        vehicleCardButton: {
            background: 'linear-gradient(135deg, #667eea, #764ba2)',
            color: '#ffffff',
            padding: '0.8rem 1.5rem',
            textDecoration: 'none',
            borderRadius: '12px',
            border: 'none',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            display: 'inline-block',
            fontWeight: '500',
            fontSize: '0.95rem'
        },
        specialSection: {
            background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
            padding: '5rem 2rem',
            textAlign: 'center',
            position: 'relative'
        },
        specialSectionOverlay: {
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 50% 50%, rgba(102, 126, 234, 0.1) 0%, transparent 70%)',
            pointerEvents: 'none'
        },
        serviceGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '2rem',
            marginTop: '3rem',
            maxWidth: '1000px',
            margin: '3rem auto 0'
        },
        serviceCard: {
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '16px',
            padding: '2rem',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            transition: 'all 0.3s ease'
        },
        serviceIcon: {
            fontSize: '3rem',
            marginBottom: '1rem',
            background: 'linear-gradient(135deg, #ff6b6b, #feca57)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
        },
        footer: {
            background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)',
            padding: '3rem 2rem 2rem',
            borderTop: '1px solid rgba(255, 255, 255, 0.1)'
        },
        footerContent: {
            maxWidth: '1200px',
            margin: '0 auto',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '2rem',
            marginBottom: '2rem'
        },
        footerSection: {
            textAlign: 'left'
        },
        footerTitle: {
            fontSize: '1.2rem',
            fontWeight: 'bold',
            marginBottom: '1rem',
            color: '#feca57'
        },
        footerLink: {
            color: '#cccccc',
            textDecoration: 'none',
            display: 'block',
            marginBottom: '0.5rem',
            transition: 'color 0.3s ease',
            whiteSpace: 'nowrap'
        },
        footerBottom: {
            textAlign: 'center',
            paddingTop: '2rem',
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            opacity: 0.7
        }
    };

    return (
        <div className="dealership-page" style={{
            ...styles.body,
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            overflow: 'auto'
        }}>
            <header style={styles.header}>
                <div style={styles.headerContent}>
                    <h1 style={styles.logo}>{dealershipName}</h1>
                    <nav style={styles.nav}>
                        <a href="#programs" className="nav-link" style={styles.navLink}>Programs</a>
                        <a href="#approval" className="nav-link" style={styles.navLink}>How It Works</a>
                        <a href="#service" className="nav-link" style={styles.navLink}>Service</a>
                        <a href="#contact" className="nav-link" style={styles.navLink}>Contact</a>
                        <a href="#approval" className="nav-link" style={styles.navLink}>Apply Now</a>
                    </nav>
                </div>
            </header>

            <section className="hero-responsive" style={styles.hero}>
                <div style={styles.heroOverlay}></div>
                <div className="hero-content-mobile" style={styles.heroContent}>
                    <h1 style={styles.heroH1}>Auto Financing Built for Real Life</h1>
                    <p style={styles.heroP}>Secure the keys to a dependable ride through {dealershipName}'s flexible financing. We fight for approvals even when your credit story has a few bumps.</p>
                    <a href="#approval" className="cta-button" style={styles.ctaButton}>Start Approval</a>
                </div>
                <div className="hero-image-container-mobile" style={styles.heroImageContainer}>
                    <img 
                        src="/assets/img/dealership/blue_honda_civic.png" 
                        alt="Blue Honda Civic - Financing Success" 
                        className="hero-car-image hero-car-image-mobile" 
                        style={styles.heroCarImage}
                    />
                </div>
            </section>

            <section id="programs" style={styles.contentSection}>
                <h2 style={styles.sectionTitle}>Financing Programs Built Around You</h2>
                <p style={{fontSize: '1.1rem', opacity: 0.85, maxWidth: '780px', margin: '0 auto 2.5rem', textAlign: 'center'}}>
                    Whether you're rebuilding credit, shopping for a family vehicle, or launching a side hustle, {dealershipName} has a personalized financing track ready for you.
                </p>
                <div style={styles.vehicleGrid}>
                    <div className="vehicle-card" style={styles.vehicleCard}>
                        <h3 style={styles.vehicleCardH3}>Smart Start Approval</h3>
                        <p style={styles.vehicleCardP}>Short application, instant document uploads, and a dedicated specialist guiding every step.</p>
                        <p style={styles.vehicleCardPrice}>Decisions in as little as 24 hours</p>
                        <a href="#approval" className="vehicle-button" style={styles.vehicleCardButton}>Learn More</a>
                    </div>
                    <div className="vehicle-card" style={styles.vehicleCard}>
                        <h3 style={styles.vehicleCardH3}>Hometown Partner Network</h3>
                        <p style={styles.vehicleCardP}>We pair your approval with trusted local banks, credit unions, and national programs to widen your options.</p>
                        <p style={styles.vehicleCardPrice}>30+ lender relationships on tap</p>
                        <a href="#contact" className="vehicle-button" style={styles.vehicleCardButton}>Meet the Team</a>
                    </div>
                    <div className="vehicle-card" style={styles.vehicleCard}>
                        <h3 style={styles.vehicleCardH3}>Credit Comeback Path</h3>
                        <p style={styles.vehicleCardP}>Structured plans that reward on-time payments and help rebuild your score with every mile.</p>
                        <p style={styles.vehicleCardPrice}>Designed for fresh financial starts</p>
                        <a href="#approval" className="vehicle-button" style={styles.vehicleCardButton}>See How</a>
                    </div>
                    <div className="vehicle-card" style={styles.vehicleCard}>
                        <h3 style={styles.vehicleCardH3}>First-Time Buyer Boost</h3>
                        <p style={styles.vehicleCardP}>Limited credit history? We highlight your employment, references, and stability to keep approvals moving.</p>
                        <p style={styles.vehicleCardPrice}>No co-signer required in many cases</p>
                        <a href="#approval" className="vehicle-button" style={styles.vehicleCardButton}>Get Started</a>
                    </div>
                    <div className="vehicle-card" style={styles.vehicleCard}>
                        <h3 style={styles.vehicleCardH3}>Trade-In Assist</h3>
                        <p style={styles.vehicleCardP}>Maximize the value of your current ride and roll equity into a low payment that feels right.</p>
                        <p style={styles.vehicleCardPrice}>Instant on-site offers available</p>
                        <a href="#contact" className="vehicle-button" style={styles.vehicleCardButton}>Talk Trade</a>
                    </div>
                    <div className="vehicle-card" style={styles.vehicleCard}>
                        <h3 style={styles.vehicleCardH3}>Essential Worker Support</h3>
                        <p style={styles.vehicleCardP}>Special consideration for teachers, healthcare, and service professionals who keep our community moving.</p>
                        <p style={styles.vehicleCardPrice}>Flexible terms tailored to your schedule</p>
                        <a href="#contact" className="vehicle-button" style={styles.vehicleCardButton}>Check Eligibility</a>
                    </div>
                </div>
            </section>

            <section id="approval" style={styles.specialSection}>
                <div style={styles.specialSectionOverlay}></div>
                <div style={{position: 'relative', zIndex: 2}}>
                    <h2 style={styles.sectionTitle}>Approval Process That's On Your Side</h2>
                    <p style={{fontSize: '1.2rem', marginBottom: '3rem', opacity: 0.9, maxWidth: '800px', margin: '0 auto 3rem'}}>
                        {dealershipName}'s neighborhood finance team celebrates real-life credit stories. We match you with lenders who look beyond the score and champion second chances.
                    </p>
                    <div style={styles.serviceGrid}>
                        <div className="service-card" style={styles.serviceCard}>
                            <div style={styles.serviceIcon}>💎</div>
                            <h3 style={{color: '#feca57', marginBottom: '1rem'}}>Real-Life Budgets</h3>
                            <p>Hand-picked programs tailor payments to your lifestyle, not just a computer-generated number.</p>
                        </div>
                        <div className="service-card" style={styles.serviceCard}>
                            <div style={styles.serviceIcon}>⚡</div>
                            <h3 style={{color: '#feca57', marginBottom: '1rem'}}>Fast Track Approvals</h3>
                            <p>Short, simple applications deliver lender decisions in minutes—often before you finish your coffee.</p>
                        </div>
                        <div className="service-card" style={styles.serviceCard}>
                            <div style={styles.serviceIcon}>🛡️</div>
                            <h3 style={{color: '#feca57', marginBottom: '1rem'}}>Lender Advocacy</h3>
                            <p>We negotiate directly with underwriters to highlight your strengths and secure a yes.</p>
                        </div>
                        <div className="service-card" style={styles.serviceCard}>
                            <div style={styles.serviceIcon}>📈</div>
                            <h3 style={{color: '#feca57', marginBottom: '1rem'}}>Credit Confidence Coaching</h3>
                            <p>Personalized tips show how each payment builds momentum toward future financial goals.</p>
                        </div>
                    </div>
                    <a href="#contact" className="cta-button" style={{
                        ...styles.ctaButton,
                        marginTop: '2rem'
                    }}>Apply for Financing</a>
                </div>
            </section>

            <section id="service" style={styles.contentSection}>
                <h2 style={styles.sectionTitle}>Service You Can Trust</h2>
                <p style={{fontSize: '1.2rem', marginBottom: '3rem', opacity: 0.9, textAlign: 'center', maxWidth: '800px', margin: '0 auto 3rem'}}>
                    Keep your ride in shape with {dealershipName}'s ASE-certified team, transparent estimates, and repairs done right the first time.
                </p>
                <div style={styles.serviceGrid}>
                    <div className="service-card" style={styles.serviceCard}>
                        <div style={styles.serviceIcon}>🔧</div>
                        <h3 style={{color: '#feca57', marginBottom: '1rem'}}>Reconditioning Pros</h3>
                        <p>Seasoned technicians inspect and service every vehicle before it ever hits the lot.</p>
                    </div>
                    <div className="service-card" style={styles.serviceCard}>
                        <div style={styles.serviceIcon}>📱</div>
                        <h3 style={{color: '#feca57', marginBottom: '1rem'}}>Easy Scheduling</h3>
                        <p>Book visits by text, phone, or online and get reminders that keep you on track.</p>
                    </div>
                    <div className="service-card" style={styles.serviceCard}>
                        <div style={styles.serviceIcon}>🚗</div>
                        <h3 style={{color: '#feca57', marginBottom: '1rem'}}>Ride Support</h3>
                        <p>Courtesy rides and loaner vehicles available when longer repairs are needed.</p>
                    </div>
                    <div className="service-card" style={styles.serviceCard}>
                        <div style={styles.serviceIcon}>✨</div>
                        <h3 style={{color: '#feca57', marginBottom: '1rem'}}>Detailing Services</h3>
                        <p>Interior and exterior details that keep your vehicle feeling new-to-you all year.</p>
                    </div>
                </div>
                <div style={{ textAlign: 'center' }}>
                    <a href="#" className="cta-button" style={{ ...styles.ctaButton, marginTop: '2rem' }}>Schedule Service</a>
                </div>
            </section>

            <footer id="contact" style={styles.footer}>
                <div style={styles.footerContent}>
                    <div style={styles.footerSection}>
                        <h3 style={styles.footerTitle}>{dealershipName}</h3>
                        <p style={{marginBottom: '1rem', opacity: 0.8}}>{dealershipName} is your independent used car dealership offering dependable vehicles backed by hometown customer care.</p>
                    </div>
                    <div style={styles.footerSection}>
                        <h3 style={styles.footerTitle}>Contact Info</h3>
                        <a href="#" className="footer-link" style={styles.footerLink}>📍 123 Main Street, Jacksonville, FL 32256</a>
                        <a href="tel:+17139798412" className="footer-link" style={styles.footerLink}>📞 (713) 979-8412</a>
                        <a href="mailto:info@galeforcemotors.com" className="footer-link" style={styles.footerLink}>✉️ info@{topic || 'galeforcemotors'}.com</a>
                    </div>
                    <div style={styles.footerSection}>
                        <h3 style={styles.footerTitle}>Quick Links</h3>
                        <a href="#programs" className="footer-link" style={styles.footerLink}>Financing Programs</a>
                        <a href="#approval" className="footer-link" style={styles.footerLink}>How It Works</a>
                        <a href="#service" className="footer-link" style={styles.footerLink}>Service Center</a>
                        <a href="#approval" className="footer-link" style={styles.footerLink}>Apply Now</a>
                    </div>
                    <div style={styles.footerSection}>
                        <h3 style={styles.footerTitle}>Legal</h3>
                        <a href="#" className="footer-link" style={styles.footerLink}>Privacy Policy</a>
                        <a href="#" className="footer-link" style={styles.footerLink}>Terms of Service</a>
                        <a href="#" className="footer-link" style={styles.footerLink}>Warranty Information</a>
                        <a href="#" className="footer-link" style={styles.footerLink}>Financing Disclosure</a>
                    </div>
                </div>
                <div style={styles.footerBottom}>
                    <p>&copy; 2025 {dealershipName}. All Rights Reserved. | Driven by Trust.</p>
                </div>
            </footer>
        </div>
    );
};

export default Dealership;
